import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminLayout from './components/AdminLayout';
import Login from './pages/Login/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/users/Users';
import AddUser from './pages/users/AddUser';
import EditUser from './pages/users/EditUser';
import ContactUs from './pages/contactUs/ContactUs';
import ViewContactUs from './pages/contactUs/ViewContactUs';
import Newsletter from './pages/newsletter/Newsletter';
import Services from './pages/services/Services';
import AddService from './pages/services/AddService';
import EditService from './pages/services/EditService';
import AddContent from './pages/content/AddContent';
import Contents from './pages/content/Contents';
import EditContent from './pages/content/EditContent';
import ViewContent from './pages/content/ViewContent';
import EmailTemplate from './pages/emailTemplate/EmailTemplate';
import EditEmailTemplate from './pages/emailTemplate/EditEmailTemplate';
import Settings from './pages/settings/Settings';
import Profile from './pages/profile/Profile';
import AddSlot from './pages/slots/AddSlot';
import EditSlot from './pages/slots/EditSlot';
import ViewSlot from './pages/slots/ViewSlot';

// New pages for comprehensive admin features
import BookingsManagement from './pages/BookingsManagement';
import PendingBookings from './pages/PendingBookings';
import ApprovedBookings from './pages/ApprovedBookings';
import CancelledBookings from './pages/CancelledBookings';
import PatientData from './pages/PatientData';
import Bookings from './pages/bookings/Bookings';
import AddBooking from './pages/bookings/AddBooking';
import EditBooking from './pages/bookings/EditBooking';
import ViewBooking from './pages/bookings/ViewBooking';
import ServiceCategories from './pages/services/ServiceCategories';
import SlotManagement from './pages/slots/Slots';
import WorkingHours from './pages/WorkingHours';
import Holidays from './pages/Holidays';
import PromoCodes from './pages/promo-codes/PromoCodes';
import AddPromoCode from './pages/promo-codes/AddPromoCode';
import DiagnosticCenters from './pages/diagnosticCenters/DiagnosticCenters';
import AddDiagnosticCenter from './pages/diagnosticCenters/AddDiagnosticCenter';
import EditDiagnosticCenter from './pages/diagnosticCenters/EditDiagnosticCenter';
import PromoAnalytics from './pages/PromoAnalytics';
import ReportUpload from './pages/ReportUpload';
import SentReports from './pages/SentReports';
import EmailTemplates from './pages/EmailTemplates';
import ContentManagement from './pages/ContentManagement';
import HomeContent from './pages/HomeContent';
import AboutContent from './pages/AboutContent';
import ContactContent from './pages/ContactContent';
import PoliciesContent from './pages/PoliciesContent';
import SEO from './pages/seo/SEO';
import SEOAdd from './pages/seo/AddSEO';
import SEOEdit from './pages/seo/EditSEO';
import MetaTags from './pages/MetaTags';
import GoogleSearchConsole from './pages/GoogleSearchConsole';
import Sitemap from './pages/Sitemap';
import Statistics from './pages/Statistics';
import MonthlyReports from './pages/MonthlyReports';
import ExportData from './pages/ExportData';
import Analytics from './pages/Analytics';
import EditPromoCode from './pages/promo-codes/EditPromoCode';
import Smtp from './pages/smtp/Smtp';
import EditSmtp from './pages/smtp/EditSmtp';
import NotificationLogs from './pages/notificationLogs/NotificationLogs';
import RadioReportParser from './pages/radioReport/RadioReportParser';

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="profile" element={<Profile />} />

          {/* Bookings Management Routes */}
          <Route path="bookings-old" element={<BookingsManagement />} />
          <Route path="bookings" element={<Bookings />} />
          <Route path="bookings/add" element={<AddBooking />} />
          <Route path="bookings/edit/:id" element={<EditBooking />} />
          <Route path="bookings/view/:id" element={<ViewBooking />} />
          <Route path="bookings/pending" element={<PendingBookings />} />
          <Route path="bookings/approved" element={<ApprovedBookings />} />
          <Route path="bookings/cancelled" element={<CancelledBookings />} />

          {/* User Management Routes */}
          <Route path="users" element={<Users />} />
          <Route path="users/add" element={<AddUser />} />
          <Route path="users/edit/:id" element={<EditUser />} />
          <Route path="users/patients" element={<PatientData />} />

          {/* Contact Form Routes */}
          <Route path="contact-us" element={<ContactUs />} />
          <Route path="contact-us/view/:id" element={<ViewContactUs />} />

          {/* Newsletter Routes */}
          <Route path="newsletter" element={<Newsletter />} />

          {/* Service Management Routes */}
          <Route path="services" element={<Services />} />
          <Route path="services/add" element={<AddService />} />
          <Route path="services/edit/:id" element={<EditService />} />
          <Route path="services/categories" element={<ServiceCategories />} />

          {/* Diagnostic Centers Routes */}
          <Route path="diagnostic-centers" element={<DiagnosticCenters />} />
          <Route
            path="diagnostic-centers/add"
            element={<AddDiagnosticCenter />}
          />
          <Route
            path="diagnostic-centers/edit/:id"
            element={<EditDiagnosticCenter />}
          />

          {/* Content Management Routes */}
          <Route path="content" element={<Contents />} />
          <Route path="content/add" element={<AddContent />} />
          <Route path="content/edit/:id" element={<EditContent />} />
          <Route path="content/view/:id" element={<ViewContent />} />

          {/* Slot Management Routes */}
          <Route path="slots" element={<SlotManagement />} />
          <Route path="slots/add" element={<AddSlot />} />
          <Route path="slots/edit/:id" element={<EditSlot />} />
          <Route path="slots/hours" element={<WorkingHours />} />
          <Route path="slots/holidays" element={<Holidays />} />
          <Route path="slots/:id" element={<ViewSlot />} />

          {/* Promo Codes Routes */}
          <Route path="promo-codes" element={<PromoCodes />} />
          <Route path="promo-codes/add" element={<AddPromoCode />} />
          <Route path="promo-codes/edit/:id" element={<EditPromoCode />} />
          <Route path="promo-codes/analytics" element={<PromoAnalytics />} />

          {/* Reports & Delivery Routes */}
          <Route path="reports" element={<ReportUpload />} />
          <Route path="reports/upload" element={<ReportUpload />} />
          <Route path="reports/sent" element={<SentReports />} />
          <Route path="reports/templates" element={<EmailTemplates />} />

          {/* Radio Report Parser */}
          <Route path="radio-report-parser" element={<RadioReportParser />} />

          {/* Content Management Routes */}
          <Route path="content" element={<ContentManagement />} />
          <Route path="content/home" element={<HomeContent />} />
          <Route path="content/about" element={<AboutContent />} />
          <Route path="content/contact" element={<ContactContent />} />
          <Route path="content/policies" element={<PoliciesContent />} />

          {/* SEO Management Routes */}
          <Route path="seo" element={<SEO />} />
          <Route path="seo/add" element={<SEOAdd />} />
          <Route path="seo/edit/:id" element={<SEOEdit />} />
          <Route path="seo/meta" element={<MetaTags />} />
          <Route path="seo/console" element={<GoogleSearchConsole />} />
          <Route path="seo/sitemap" element={<Sitemap />} />

          {/* Statistics & Export Routes */}
          <Route path="statistics" element={<Statistics />} />
          <Route path="statistics/monthly" element={<MonthlyReports />} />
          <Route path="statistics/export" element={<ExportData />} />
          <Route path="statistics/analytics" element={<Analytics />} />

          {/* Email Template Management Routes */}
          <Route path="email-templates" element={<EmailTemplate />} />
          <Route
            path="email-templates/edit/:id"
            element={<EditEmailTemplate />}
          />
          <Route path="notification-logs" element={<NotificationLogs />} />
          <Route path="smtp" element={<Smtp />} />
          <Route path="smtp/edit/:id" element={<EditSmtp />} />

          <Route path="settings" element={<Settings />} />
        </Route>
      </Routes>
    </AuthProvider>
  );
};

export default App;
